import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { MessageSquare, X, Send, Sparkles } from "lucide-react";
import { trackEvent } from "./GAListener";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const STORAGE_KEY = "tdm_chat_session_id";

const SEED_SUGGESTIONS = [
  "What does Abhinav do?",
  "Should I buy the Playbook or book a call?",
  "How is AI changing B2B marketing?",
];

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      text: "Hi — I'm Abhinav's AI concierge. Ask me anything about his work, the ebooks, the ad audits, or which essay to read first.",
    },
  ]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [sessionId, setSessionId] = useState(() => {
    if (typeof window === "undefined") return null;
    return window.localStorage.getItem(STORAGE_KEY);
  });
  const scrollerRef = useRef(null);

  useEffect(() => {
    if (scrollerRef.current) {
      scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
    }
  }, [messages, open]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || sending) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", text: msg }]);
    setSending(true);
    try {
      const res = await axios.post(`${API}/chat`, { session_id: sessionId, message: msg }, { timeout: 45000 });
      const { reply, session_id } = res.data;
      if (session_id && session_id !== sessionId) {
        setSessionId(session_id);
        window.localStorage.setItem(STORAGE_KEY, session_id);
      }
      setMessages((m) => [...m, { role: "assistant", text: reply }]);
      trackEvent("chat_message_sent", { turn: messages.filter((x) => x.role === "user").length + 1 });
    } catch (err) {
      setMessages((m) => [
        ...m,
        {
          role: "assistant",
          text: "Sorry — I hit a snag reaching my brain. Please try again, or use the contact form at the bottom of the page.",
        },
      ]);
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      {/* Launcher */}
      <button
        onClick={() => {
          setOpen((v) => !v);
          if (!open) trackEvent("chat_opened");
        }}
        data-testid="chat-launcher"
        aria-label={open ? "Close chat" : "Open chat"}
        className="fixed bottom-6 right-6 z-40 w-14 h-14 md:w-16 md:h-16 bg-ink text-paper hover:bg-signal transition-colors flex items-center justify-center shadow-[6px_6px_0_0_#E63946] hover:shadow-[8px_8px_0_0_#0A0A0A]"
      >
        {open ? <X size={22} /> : <MessageSquare size={22} />}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 16, scale: 0.98 }}
            transition={{ duration: 0.25, ease: [0.2, 0.7, 0.2, 1] }}
            data-testid="chat-panel"
            className="fixed bottom-24 right-6 z-40 w-[calc(100vw-3rem)] sm:w-[400px] max-w-[420px] h-[560px] max-h-[calc(100vh-9rem)] bg-paper border border-ink shadow-[10px_10px_0_0_#0A0A0A] flex flex-col"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-ink bg-ink text-paper">
              <div className="flex items-center gap-3">
                <Sparkles className="w-4 h-4 text-signal" strokeWidth={2} />
                <div className="leading-tight">
                  <div className="font-heading font-black text-[15px] tracking-tight">
                    Ask the concierge
                  </div>
                  <div className="font-mono text-[9px] uppercase tracking-[0.22em] text-paper/60">
                    Claude · powered by Abhinav&apos;s brain
                  </div>
                </div>
              </div>
              <button
                onClick={() => setOpen(false)}
                aria-label="Close"
                className="text-paper/70 hover:text-signal transition-colors"
              >
                <X size={18} />
              </button>
            </div>

            {/* Messages */}
            <div
              ref={scrollerRef}
              className="flex-1 overflow-y-auto px-5 py-5 space-y-4 bg-paper"
              data-testid="chat-messages"
            >
              {messages.map((m, i) => (
                <div
                  key={i}
                  className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
                >
                  <div
                    className={`max-w-[85%] px-4 py-3 font-mono text-[13px] leading-[1.55] whitespace-pre-wrap ${
                      m.role === "user"
                        ? "bg-ink text-paper"
                        : "bg-surface text-ink border-l-2 border-signal"
                    }`}
                  >
                    {m.text}
                  </div>
                </div>
              ))}
              {sending && (
                <div className="flex justify-start">
                  <div className="bg-surface text-ink border-l-2 border-signal px-4 py-3 font-mono text-[13px] inline-flex gap-1">
                    <span className="animate-pulse">●</span>
                    <span className="animate-pulse" style={{ animationDelay: "150ms" }}>●</span>
                    <span className="animate-pulse" style={{ animationDelay: "300ms" }}>●</span>
                  </div>
                </div>
              )}
              {messages.length === 1 && !sending && (
                <div className="pt-2 space-y-2">
                  <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-ink2 mb-2">
                    Try asking
                  </div>
                  {SEED_SUGGESTIONS.map((s) => (
                    <button
                      key={s}
                      onClick={() => send(s)}
                      data-testid={`chat-suggest-${s.slice(0, 12).replace(/\s/g, '-')}`}
                      className="block w-full text-left px-3 py-2 font-mono text-[12px] uppercase tracking-[0.14em] text-ink border border-ink/20 hover:border-ink hover:bg-surface transition-colors"
                    >
                      {s}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Input */}
            <form
              onSubmit={(e) => {
                e.preventDefault();
                send();
              }}
              className="border-t border-ink p-3 flex gap-2 bg-paper"
            >
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Type your question…"
                disabled={sending}
                data-testid="chat-input"
                className="flex-1 bg-transparent border border-ink/30 focus:border-ink px-3 py-2 font-mono text-[13px] text-ink placeholder:text-ink2/60 outline-none disabled:opacity-60"
                maxLength={2000}
              />
              <button
                type="submit"
                disabled={sending || !input.trim()}
                data-testid="chat-send"
                className="bg-ink text-paper hover:bg-signal disabled:opacity-40 disabled:cursor-not-allowed px-4 py-2 flex items-center gap-2 font-heading font-bold text-[11px] uppercase tracking-[0.2em] transition-colors"
              >
                <Send size={13} />
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
